/**
 * =============================================================================
 * HEADER COMPONENT - Main Navigation Bar
 * =============================================================================
 * 
 * The primary navigation component displayed at the top of every page.
 * Provides navigation, user actions, and responsive mobile menu.
 * 
 * FEATURES:
 * - Sticky positioning with backdrop blur
 * - Unread message counter with real-time updates
 * - Responsive design (mobile hamburger menu, desktop inline buttons)
 * - Admin menu section for administrators
 * - Platform-specific styling (iOS safe areas)
 * - Scroll shadow effect for depth perception
 * 
 * STRUCTURE:
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  [Back?] [Logo] HittaDelar          [Saved] [Messages] [+] [≡]  │
 * └──────────────────────────────────────────────────────────────────┘
 * 
 * EXTENDING:
 * To add new navigation items:
 * 1. For logged-in users: Add to the user dropdown menu
 * 2. For logged-out users: Add to the guest dropdown menu
 * 3. For both: Add as standalone buttons in the nav section
 * 
 * @see src/components/Layout.tsx - Parent component
 * @see src/hooks/useAdminRole.ts - Admin role checking
 * @see src/hooks/usePlatform.ts - Platform detection
 * =============================================================================
 */

import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Car, 
  Plus, 
  LogOut, 
  User, 
  Settings as SettingsIcon, 
  Heart, 
  Menu, 
  Mail, 
  Home, 
  ArrowLeft, 
  ShieldAlert,
  ShoppingBag,
  Package,
  FileText,
  X,
  ChevronLeft
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger, 
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { PlatformOptimizedButton } from "@/components/PlatformOptimizedButton";
import { usePlatform } from "@/hooks/usePlatform";
import { cn } from "@/lib/utils";
import { useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { NotificationDot } from "@/components/NotificationDot";
import { useNotificationCounts } from "@/hooks/useNotificationCounts";
import { useAdminRole } from "@/hooks/useAdminRole";
import { ListingDraft, hasDraft, getDrafts, getDraftLabel, deleteDraft } from "@/hooks/useListingDraft";
import { checkNavigationGuard } from "@/hooks/useNavigationGuard";
import hittadelarLogo from "@/assets/hittadelar-logo-main.png";

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/** "Sparad för 3 min sedan" / "Sparad igår kl. 14:20" / "Sparad 3 sep kl. 09:15" */
const formatDraftTime = (ts: number): string => {
  const now = new Date();
  const d = new Date(ts);
  const diffMin = Math.floor((now.getTime() - ts) / 60000);
  const clock = d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
  if (diffMin < 1) return "Sparad nyss";
  if (diffMin < 60) return `Sparad för ${diffMin} min sedan`;
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `Sparad idag kl. ${clock}`;
  const yesterday = new Date(now.getTime() - 86400000);
  if (d.toDateString() === yesterday.toDateString()) return `Sparad igår kl. ${clock}`;
  const dayMonth = d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
  return `Sparad ${dayMonth} kl. ${clock}`;
};

interface HeaderProps {
  /** Current authenticated user object (null if not logged in) */
  user: any;
  /** Show back navigation arrow (default: false) */
  showBackButton?: boolean;
  /** Show "Lägg till annons" button (default: true) */
  showCreateButton?: boolean;
  /** Show "Sparade" saved listings button (default: true) */
  showSavedButton?: boolean;
}

// =============================================================================
// HEADER COMPONENT
// =============================================================================

export const Header = ({ 
  user, 
  showBackButton = false, 
  showCreateButton = true, 
  showSavedButton = true 
}: HeaderProps) => {
  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  
  const navigate = useNavigate();
  const location = useLocation();
  // På inloggningssidan (t.ex. under tvåstegsverifiering) ska sidhuvudet
  // visas som utloggat även om en teknisk session finns aktiv.
  if (location.pathname === '/auth') {
    user = null;
  }
  const guardedNavigate = useCallback((to: string | number) => {
    if (typeof to === 'string' && checkNavigationGuard(to)) return;
    navigate(to as any);
  }, [navigate]);
  const { isIOS, isMobile } = usePlatform();
  const { isAdmin } = useAdminRole();
  
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  
  /** Count of unread message threads */
  const [unreadCount, setUnreadCount] = useState(0);
  const { counts: notif } = useNotificationCounts();
  const sellNotif = notif.sell_listings + notif.sell_ongoing + notif.sell_done;
  const buyNotif = notif.buy_ongoing + notif.buy_done;
  const messagesNotif = unreadCount + notif.disputes;
  
  /** Track if page has been scrolled (for shadow effect) */
  const [isScrolled, setIsScrolled] = useState(false);

  /** Force re-render key when drafts change */
  const [draftKey, setDraftKey] = useState(0);

  /** Drafts loaded from the backend */
  const [drafts, setDrafts] = useState<ListingDraft[]>([]);

  // ---------------------------------------------------------------------------
  // Scroll Shadow Effect
  // ---------------------------------------------------------------------------
  
  /**
   * Add shadow to header when user scrolls down.
   * Uses requestAnimationFrame throttling for smooth 60fps performance.
   */
  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 0);
          rafId = null;
        });
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Unread Messages Counter
  // ---------------------------------------------------------------------------
  
  /**
   * Fetch and subscribe to unread message count.
   * Updates in real-time when new messages arrive.
   */
  useEffect(() => {
    if (user) {
      checkUnreadMessages();
      
      // Subscribe to new messages for real-time updates
      const channel = supabase
        .channel(`header-messages:${user.id}`, { config: { private: true } })
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${user.id}`,
          },
          () => {
            checkUnreadMessages();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  /**
   * Load drafts from the backend whenever the user or draftKey changes.
   */
  useEffect(() => {
    if (user) {
      getDrafts().then(setDrafts).catch(() => setDrafts([]));
    } else {
      setDrafts([]);
    }
  }, [user, draftKey]);

  /**
   * Delete a draft and refresh the list.
   */
  const handleDeleteDraft = async (draftId: string) => {
    await deleteDraft(draftId);
    setDraftKey(k => k + 1);
  };

  /**
   * Count unique threads with unread messages.
   * A thread is defined by: listing_id + other_user_id combination.
   */
  const checkUnreadMessages = async () => {
    if (!user) return;

    // Fetch all messages involving the current user
    const { data: messages } = await supabase
      .from('messages')
      .select(`
        *,
        sender:sender_id(id),
        receiver:receiver_id(id)
      `)
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (!messages) {
      setUnreadCount(0);
      return;
    }

    // Group messages by thread (listing + other user)
    const threadMap = new Map<string, { hasUnread: boolean; messageCount: number }>();

    messages.forEach((msg: any) => {
      const otherUserId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
      const threadKey = `${msg.listing_id}-${otherUserId}`;

      if (!threadMap.has(threadKey)) {
        threadMap.set(threadKey, {
          hasUnread: msg.receiver_id === user.id && !msg.read_at,
          messageCount: 1
        });
      } else {
        const thread = threadMap.get(threadKey)!;
        thread.messageCount++;
        // Mark thread as unread if ANY message is unread
        if (msg.receiver_id === user.id && !msg.read_at) {
          thread.hasUnread = true;
        }
      }
    });

    // Count threads with unread messages
    let totalUnreadThreads = 0;
    threadMap.forEach((thread) => {
      if (thread.hasUnread) {
        totalUnreadThreads++;
      }
    });

    setUnreadCount(totalUnreadThreads);
  };

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------
  
  /**
   * Sign out the current user and redirect to homepage.
   */
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <header className={cn(
      // Base styles: sticky, blur background, border
      "sticky top-0 z-50 bg-background border-b backdrop-blur supports-[backdrop-filter]:bg-background/95 transition-shadow duration-200",
      // iOS safe area padding (for notch/dynamic island)
      isIOS && isMobile && "ios-safe-area-top",
      // Add shadow when scrolled
      isScrolled && "shadow-md"
    )}>
      <div className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center justify-between">
          
          {/* ===============================================================
              LEFT SECTION: Back button + Logo
          =============================================================== */}
          <div className="flex items-center gap-2">
            {/* Optional back button for detail pages */}
            {showBackButton && (
              <PlatformOptimizedButton variant="ghost" size="icon" aria-label="Gå tillbaka" onClick={() => guardedNavigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </PlatformOptimizedButton>
            )}
            
            {/* Logo/Brand - clickable to go home */}
            <div 
              className="flex items-center gap-2 cursor-pointer no-select" 
              onClick={() => guardedNavigate("/")}
            >
              <img src={hittadelarLogo} alt="HittaDelar" className="h-8 w-8 bg-white rounded-full" />
              <span className="text-2xl font-bold">HittaDelar</span>
            </div>
          </div>
          
          {/* ===============================================================
              RIGHT SECTION: Navigation buttons
          =============================================================== */}
          <nav className="flex items-center gap-2">
            {user ? (
              /* ---------------------------------------------------------
                 LOGGED IN USER NAVIGATION
              --------------------------------------------------------- */
              <>
                {/* Saved listings button (desktop only) */}
                {showSavedButton && (
                  <PlatformOptimizedButton 
                    onClick={() => guardedNavigate("/saved")} 
                    variant="ghost" 
                    className="hidden md:flex"
                  >
                    <Heart className="h-4 w-4 mr-1 fill-blue-500 text-blue-500" />
                    Sparade
                  </PlatformOptimizedButton>
                )}
                
                {/* Messages button with unread badge */}
                <PlatformOptimizedButton 
                  onClick={() => guardedNavigate("/messages")} 
                  variant="ghost" 
                  size="icon"
                  aria-label="Meddelanden"
                  className="relative"
                >
                  <Mail className="h-5 w-5" />
                  {messagesNotif > 0 && (
                    <Badge 
                      variant="default" 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px] font-semibold rounded-full"
                    >
                      {messagesNotif}
                    </Badge>
                  )}
                </PlatformOptimizedButton>
                
                {/* Create listing button with draft menu */}
                {showCreateButton && (
                  <>
                    {/* Desktop: Drafts menu if drafts exist, otherwise direct button */}
                    {drafts.length > 0 ? (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <PlatformOptimizedButton className="hidden md:flex relative">
                            <Plus className="h-4 w-4 mr-1" />
                            Lägg till annons
                            <NotificationDot count={drafts.length} variant="inverted" className="absolute -top-1.5 -right-1.5" />
                          </PlatformOptimizedButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-80 max-w-[92vw] bg-background border z-50 rounded-2xl shadow-xl overflow-hidden p-0">
                          <DropdownMenuItem onClick={() => guardedNavigate("/create-listing")} className="cursor-pointer rounded-none border-b flex items-center gap-4 p-4 focus:bg-accent">
                            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                              <Plus className="h-5 w-5" />
                            </div>
                            <div className="text-left">
                              <p className="font-bold leading-tight">Ny annons</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Börja på en helt ny annons</p>
                            </div>
                          </DropdownMenuItem>

                          <div className="px-4 py-2.5 bg-muted/50 border-b">
                            <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Påbörjade annonser</h3>
                          </div>

                          {drafts.map((draft) => (
                            <DropdownMenuItem key={draft.id} onClick={() => guardedNavigate(`/create-listing?draft=${draft.id}`)} className="cursor-pointer rounded-none flex items-start gap-3 p-4 focus:bg-accent group/draft">
                              {draft.thumbnail ? (
                                <img src={draft.thumbnail} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border bg-secondary" />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-secondary border border-dashed border-muted-foreground/40 flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-6 h-6 text-muted-foreground/50" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className={cn("text-sm font-semibold truncate", !draft.formData.title?.trim() && "text-muted-foreground italic")}>
                                  {getDraftLabel(draft)}
                                </h4>
                                <p className="text-[11px] text-muted-foreground mt-0.5">{formatDraftTime(draft.savedAt)}</p>
                                <div className="flex items-center gap-3 mt-1.5">
                                  <span className="text-xs font-semibold text-primary">Fortsätt</span>
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-muted-foreground hover:text-destructive transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteDraft(draft.id);
                                    }}
                                  >
                                    Ta bort
                                  </button>
                                </div>
                              </div>
                            </DropdownMenuItem>
                          ))}

                          <DropdownMenuItem className="cursor-pointer rounded-none border-t justify-center py-3.5 text-sm text-muted-foreground hover:text-foreground focus:bg-accent">
                            Stäng menyn
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <PlatformOptimizedButton 
                        onClick={() => guardedNavigate("/create-listing")} 
                        className="hidden md:flex"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Lägg till annons
                      </PlatformOptimizedButton>
                    )}
                    
                    {/* Mobile: Same drafts menu, icon trigger */}
                    {drafts.length > 0 ? (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <PlatformOptimizedButton 
                            size="icon"
                            aria-label="Skapa annons"
                            className="md:hidden relative bg-primary hover:bg-primary/90 text-primary-foreground"
                          >
                            <Plus className="h-5 w-5" />
                            <NotificationDot count={drafts.length} variant="inverted" className="absolute -top-1.5 -right-1.5" />
                          </PlatformOptimizedButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-80 max-w-[92vw] bg-background border z-50 rounded-2xl shadow-xl overflow-hidden p-0">
                          <DropdownMenuItem onClick={() => guardedNavigate("/create-listing")} className="cursor-pointer rounded-none border-b flex items-center gap-4 p-4 focus:bg-accent">
                            <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                              <Plus className="h-5 w-5" />
                            </div>
                            <div className="text-left">
                              <p className="font-bold leading-tight">Ny annons</p>
                              <p className="text-xs text-muted-foreground mt-0.5">Börja på en helt ny annons</p>
                            </div>
                          </DropdownMenuItem>

                          <div className="px-4 py-2.5 bg-muted/50 border-b">
                            <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Påbörjade annonser</h3>
                          </div>

                          {drafts.map((draft) => (
                            <DropdownMenuItem key={draft.id} onClick={() => guardedNavigate(`/create-listing?draft=${draft.id}`)} className="cursor-pointer rounded-none flex items-start gap-3 p-4 focus:bg-accent">
                              {draft.thumbnail ? (
                                <img src={draft.thumbnail} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border bg-secondary" />
                              ) : (
                                <div className="w-16 h-16 rounded-lg bg-secondary border border-dashed border-muted-foreground/40 flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-6 h-6 text-muted-foreground/50" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className={cn("text-sm font-semibold truncate", !draft.formData.title?.trim() && "text-muted-foreground italic")}>
                                  {getDraftLabel(draft)}
                                </h4>
                                <p className="text-[11px] text-muted-foreground mt-0.5">{formatDraftTime(draft.savedAt)}</p>
                                <div className="flex items-center gap-3 mt-1.5">
                                  <span className="text-xs font-semibold text-primary">Fortsätt</span>
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-muted-foreground hover:text-destructive transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteDraft(draft.id);
                                    }}
                                  >
                                    Ta bort
                                  </button>
                                </div>
                              </div>
                            </DropdownMenuItem>
                          ))}

                          <DropdownMenuItem className="cursor-pointer rounded-none border-t justify-center py-3.5 text-sm text-muted-foreground hover:text-foreground focus:bg-accent">
                            Stäng menyn
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <PlatformOptimizedButton 
                        onClick={() => guardedNavigate("/create-listing")} 
                        size="icon"
                        aria-label="Skapa annons"
                        className="md:hidden bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        <Plus className="h-5 w-5" />
                      </PlatformOptimizedButton>
                    )}
                  </>
                )}
                
                {/* Hamburger menu dropdown */}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <PlatformOptimizedButton variant="outline" size="icon" aria-label="Öppna meny" className="relative">
                      <Menu className="h-5 w-5" />
                      <NotificationDot count={sellNotif + buyNotif} className="absolute -top-2 -right-2" />
                    </PlatformOptimizedButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-background border z-50">
                    {/* Home link */}
                    <DropdownMenuItem onClick={() => guardedNavigate("/")} className="cursor-pointer">
                      <Home className="h-4 w-4 mr-2" />
                      Start
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    
                    {/* Mobile-only: Saved listings */}
                    {showSavedButton && (
                      <DropdownMenuItem 
                        onClick={() => guardedNavigate("/saved")} 
                        className="cursor-pointer md:hidden"
                      >
                        <Heart className="h-4 w-4 mr-2 fill-blue-500 text-blue-500" />
                        Sparade
                      </DropdownMenuItem>
                    )}
                    
                    {/* Mobile-only: Create listing with draft options */}
                    {showCreateButton && drafts.length > 0 ? (
                      <>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="cursor-pointer md:hidden [&>svg:last-child]:hidden focus:text-accent-foreground data-[state=open]:text-accent-foreground">
                            <ChevronLeft className="h-4 w-4 mr-2" />
                            Påbörjade annonser
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-36 max-w-[45vw] bg-background border z-50">
                            {drafts.map((draft) => (
                              <DropdownMenuItem 
                                key={draft.id}
                                onClick={() => guardedNavigate(`/create-listing?draft=${draft.id}`)} 
                                className="cursor-pointer flex items-center justify-between gap-2"
                              >
                                {draft.thumbnail ? (
                                  <img src={draft.thumbnail} alt="" className="h-6 w-6 rounded object-cover flex-shrink-0" />
                                ) : (
                                  <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                                )}
                                <span className="truncate flex-1">{getDraftLabel(draft)}</span>
                                <button
                                  type="button"
                                  aria-label="Ta bort utkast"
                                  className="ml-2 p-1 rounded-sm hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex-shrink-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteDraft(draft.id);
                                  }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuItem 
                          onClick={() => guardedNavigate("/create-listing")} 
                          className="cursor-pointer md:hidden"
                        >
                          <Plus className="h-4 w-4 mr-2 text-primary" />
                          Ny annons
                        </DropdownMenuItem>
                      </>
                    ) : showCreateButton ? (
                      <DropdownMenuItem 
                        onClick={() => guardedNavigate("/create-listing")} 
                        className="cursor-pointer md:hidden"
                      >
                        <Plus className="h-4 w-4 mr-2 text-primary" />
                        Lägg till annons
                      </DropdownMenuItem>
                    ) : null}
                    
                    {/* My listings */}
                    <DropdownMenuItem onClick={() => guardedNavigate("/dashboard")} className="cursor-pointer">
                      <Package className="h-4 w-4 mr-2" />
                      Mina annonser
                      <NotificationDot count={sellNotif} className="ml-auto" />
                    </DropdownMenuItem>
                    
                    {/* My purchases */}
                    <DropdownMenuItem onClick={() => guardedNavigate("/purchases")} className="cursor-pointer">
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Mina köp
                      <NotificationDot count={buyNotif} className="ml-auto" />
                    </DropdownMenuItem>
                    
                    {/* Account settings */}
                    <DropdownMenuItem onClick={() => guardedNavigate("/account")} className="cursor-pointer">
                      <User className="h-4 w-4 mr-2" />
                      Mitt konto
                    </DropdownMenuItem>
                    
                    {/* ---------------------------------------------------
                        ADMIN SECTION
                        Only visible to users with admin role
                    --------------------------------------------------- */}
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => guardedNavigate("/admin")} 
                          className="cursor-pointer text-primary"
                        >
                          <ShieldAlert className="h-4 w-4 mr-2" />
                          Admin
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    <DropdownMenuSeparator />
                    
                    {/* Logout */}
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logga ut
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              /* ---------------------------------------------------------
                 LOGGED OUT USER NAVIGATION
              --------------------------------------------------------- */
              <>
                {/* Guest hamburger menu */}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <PlatformOptimizedButton variant="outline" size="icon" aria-label="Öppna meny">
                      <Menu className="h-5 w-5" />
                    </PlatformOptimizedButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-background border z-50">
                    <DropdownMenuItem onClick={() => guardedNavigate("/")} className="cursor-pointer">
                      <Home className="h-4 w-4 mr-2" />
                      Start
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => guardedNavigate("/contact")} className="cursor-pointer">
                      <Mail className="h-4 w-4 mr-2" />
                      Kontakt
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => guardedNavigate("/auth")} className="cursor-pointer">
                      <User className="h-4 w-4 mr-2" />
                      Logga in
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => guardedNavigate("/auth?tab=signup")} className="cursor-pointer">
                      <Plus className="h-4 w-4 mr-2" />
                      Skapa konto
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Desktop-only: Login/Signup buttons */}
                <PlatformOptimizedButton 
                  onClick={() => guardedNavigate("/auth")} 
                  variant="outline" 
                  className="hidden md:flex"
                >
                  Logga in
                </PlatformOptimizedButton>
                <PlatformOptimizedButton 
                  onClick={() => guardedNavigate("/auth?tab=signup")} 
                  className="hidden md:flex"
                >
                  Skapa konto
                </PlatformOptimizedButton>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};
